import type { IAuthorization } from '~/domain/Credentials/types/Credentials';
import { useGetAuthorizationProfessional } from '~/domain/Credentials';
import { IJob, IUser, ProfessionTypeEnum } from '~/models';

interface IUseBookedProfessionalCredentialsArgs {
  job: Partial<IJob> | null | undefined;
  /** The professional who held the shift, or `undefined` on an unfilled one. */
  professional: IUser | null | undefined;
}

/**
 * The booked professional's verified credentials, for the Credentials tab.
 *
 * Goes straight to the credentials domain rather than through
 * `useConfirmCandidateData` (which the Confirm Candidates page uses): that hook
 * also fetches the shift's applicant pools to find the candidate inside them,
 * and on a terminal shift there is no decision left for a pool to inform — the
 * professional is already named on the shift. This asks only for the
 * credentials.
 *
 * The domain hook keys its query on `professionalId` and skips the request when
 * it is empty, so the unfilled variants make no credentials call at all.
 */
export const useBookedProfessionalCredentials = ({
  job,
  professional,
}: IUseBookedProfessionalCredentialsArgs) => {
  const { authorizations } = useGetAuthorizationProfessional({
    professionalId: professional?.id ?? '',
    candidateLicense: professional?.license || professional?.licenses?.[0],
    jobProfessionName: job?.professionName,
    jobSubProfession: job?.subProfession,
    offerOwnerId: job?.offer_owner?.id,
    jobProfession: job?.profession as ProfessionTypeEnum | undefined,
    jobState: job?.state,
  });

  return { authorizations: (authorizations ?? null) as IAuthorization[] | null };
};
